// OrientationPrompt — "please rotate your device" overlay.
//
// Rendered on top of a game when `useGameOrientationLock` reports
// the live orientation doesn't match the game's required orientation
// AND the hardware lock didn't take effect (iOS Safari, locked-axis
// PWA misses, etc.).
//
// One component covers BOTH cases via the `required` prop:
//   • required='portrait'  → shows a horizontal-phone glyph rotating
//                            to vertical, with "rotate to portrait".
//   • required='landscape' → mirror image, "rotate to landscape".
//
// The overlay covers the entire viewport at z-[70] so it stacks over
// the game's loading screen (z-60) when both happen at once — the
// player must rotate first.
//
// Visual is intentionally minimal so it doesn't compete with the
// game's identity: a single animated phone outline + label. Same
// look across all games.

import Icon from './Icon';

type Props = {
  required: 'portrait' | 'landscape';
  /** Optional "back to home" affordance. When provided, renders a
   *  small back button in the safe-area so a user who landed here by
   *  mistake can bail out without rotating. */
  onExit?: () => void;
  /** Overrides the default "ROTATE YOUR DEVICE" headline. */
  label?: string;
  /** Overrides the smaller helper line. */
  sublabel?: string;
};

export default function OrientationPrompt({
  required, onExit, label, sublabel,
}: Props) {
  // The phone glyph rotates from the WRONG orientation to the RIGHT
  // one over 1.6s, holds, then loops. Implemented as a 2-frame keyframe
  // so the player sees the gesture they need to perform.
  const fromAngle = required === 'portrait' ? 90 : 0;
  const toAngle   = required === 'portrait' ? 0  : 90;

  const defaultSub = required === 'portrait'
    ? 'This game is designed for upright play.'
    : 'This game is designed for sideways play.';

  return (
    <div
      role="alertdialog"
      aria-label={label || 'Rotate your device'}
      className="fixed inset-0 z-[70] flex items-center justify-center
                 bg-black/90 text-white select-none"
      style={{ touchAction: 'none' }}
    >
      {onExit && (
        <button
          type="button"
          onClick={onExit}
          aria-label="Back to home"
          title="Back to home"
          className="absolute left-3 w-10 h-10 rounded-full flex items-center justify-center
                     bg-white/10 border border-white/15 hover:bg-white/20 active:scale-95
                     transition text-white"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
        >
          <Icon name="arrow_back" size={20} filled />
        </button>
      )}

      <div className="flex flex-col items-center gap-5 px-6 text-center">
        {/* Phone glyph — outlined rectangle with a tiny notch + dot.
            Sized in CSS so the rotate animation can pivot around its
            geometric centre. */}
        <div
          className="op-phone"
          style={{
            // Custom properties feed the keyframe interpolator so the
            // same component can animate to either target angle.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ['--op-from' as any]: `${fromAngle}deg`,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ['--op-to'   as any]: `${toAngle}deg`,
          }}
          aria-hidden
        >
          <div className="op-phone__notch" />
          <div className="op-phone__screen">
            <div className="op-phone__dot" />
          </div>
        </div>

        <div className="font-display font-extrabold tracking-[.2em] uppercase text-[15px]">
          {label || 'Rotate your device'}
        </div>
        <div className="text-[11px] opacity-70 leading-snug max-w-[22ch]">
          {sublabel || defaultSub}
        </div>
      </div>

      <style>{`
        @keyframes op-rotate {
          0%   { transform: rotate(var(--op-from)); }
          40%  { transform: rotate(var(--op-to)); }
          85%  { transform: rotate(var(--op-to)); }
          100% { transform: rotate(var(--op-from)); }
        }
        .op-phone {
          position: relative;
          width: 56px;
          height: 94px;
          border: 2.5px solid rgba(255,255,255,0.85);
          border-radius: 12px;
          animation: op-rotate 2.4s cubic-bezier(.65,.05,.36,1) infinite;
          background: rgba(255,255,255,0.04);
          box-shadow: 0 6px 18px rgba(0,0,0,0.5);
          transform-origin: 50% 50%;
        }
        .op-phone__notch {
          position: absolute; left: 50%; top: 5px;
          width: 18px; height: 4px; border-radius: 2px;
          background: rgba(255,255,255,0.7);
          transform: translateX(-50%);
        }
        .op-phone__screen {
          position: absolute; inset: 14px 5px 11px 5px;
          border-radius: 5px;
          background: rgba(255,255,255,0.06);
          display: flex; align-items: center; justify-content: center;
        }
        .op-phone__dot {
          width: 10px; height: 10px; border-radius: 50%;
          background: rgba(255,255,255,0.85);
          animation: op-rotate 2.4s cubic-bezier(.65,.05,.36,1) infinite reverse;
        }
      `}</style>
    </div>
  );
}
